/// <amd-module name="Feed/Legacy/ControllerViews/SettingsControllerView" />
import * as React from "react";
import { connect } from "react-redux";

import { SelectionMode } from "OfficeFabric/Selection";
import { findIndex } from "OfficeFabric/Utilities";

import * as Utils_String from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { HubHeader } from "VSSUI/Components/HubHeader/HubHeader";
import { IVssIconProps } from "VSSUI/Components/VssIcon";
import { Dialog } from "VSSUI/Dialog";
import { FilterBar, IFilterBarProps } from "VSSUI/FilterBar";
import { Hub, IHub } from "VSSUI/Hub";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { IObservableViewStateUrl } from "VSSUI/Navigation/ViewStateNavigation";
import { PickListFilterBarItem } from "VSSUI/PickList";
import { IPivotBarAction, PivotBarFocusItem, PivotBarItem } from "VSSUI/PivotBar";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";
import { HubViewStateEventNames } from "VSSUI/Utilities/HubViewState";
import { VssIconType } from "VSSUI/VssIcon";

import { FeedSettingsCi, FwLinks, SettingsFilterItemKey, SettingsPivotKeys } from "Feed/Common/Constants/Constants";
import { FeedView, FeedViewType } from "Feed/Common/Contracts/Feed";
import { UpstreamHealthStatus } from "Feed/Common/Contracts/WebPage";
import * as PackageResources from "Feed/Common/Resources";
import { FeedHubActions, getUpstreamHealthStatuses, IFeedHubViewAwareState } from "Feed/Core/Redux/FeedHub";
import * as LegacyActions from "Feed/Legacy/Actions/Actions";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { FeedDetailsPane, IFeedDetailsPaneProps } from "Feed/Legacy/Components/Settings/FeedDetailsPane";
import { IFeedSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { MapIFeedSettingsStateToProps } from "Feed/Legacy/Components/Settings/MapIFeedSettingsStateToProps";
import { IPermissionsProps, Permissions } from "Feed/Legacy/Components/Settings/Permissions";
import { UpstreamSettingsFilterBar } from "Feed/Legacy/Components/Settings/UpstreamSettingsFilterBar";
import { IUpstreamSettingsPaneProps, UpstreamSettingsPane } from "Feed/Legacy/Components/Settings/UpstreamSettingsPane";
import { IViewsProps, Views } from "Feed/Legacy/Components/Settings/Views";
import { WrappedZeroFeedsPage } from "Feed/Legacy/Components/WrappedZeroFeedsPage";
import { ILegacyControllerViewProps } from "Feed/Legacy/ControllerViews/ILegacyControllerViewProps";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { FeedStore } from "Feed/Legacy/Stores/FeedStore";
import { IUpstreamSettingsRowData } from "Feed/Legacy/Types/IUpstreamSettingsRowData";

import { HubWebPageDataService } from "../DataServices/WebPageDataService";

const Actions = {
    loadFeedMessageStarted: FeedHubActions.loadFeedMessageStarted
};

interface ISettingsControllerViewProps {
    fluxStore: FeedStore;
}

interface ISettingsControllerViewProps2 {
    upstreamHealthStatuses?: UpstreamHealthStatus[];
}

export class SettingsControllerView extends VssComponent<
    ISettingsControllerViewProps & ISettingsControllerViewProps2 & typeof Actions,
    IFeedSettingsState> {
    private _projectScopedUpstreamsEnabled: boolean;

    constructor(
        props: ISettingsControllerViewProps & ISettingsControllerViewProps2 & typeof Actions,
        context?: IVssComponentContext) {
        super(props, context);

        this.getState().pageContext = context!.pageContext;
        this.state = this.getState();
        this._pivotUrls = {};
        [
            SettingsPivotKeys.details,
            SettingsPivotKeys.permissions,
            SettingsPivotKeys.views,
            SettingsPivotKeys.upstreams
        ].forEach((pivotKey: string) => {
            this._pivotUrls[pivotKey] = this.state.hubViewState.createObservableUrl({ view: pivotKey });
        });

        const webPageDataService = this.context.pageContext.getService<HubWebPageDataService>(
            "IFeedWebPageDataService"
        );
        this._projectScopedUpstreamsEnabled = webPageDataService.isProjectScopedUpstreamsEnabled();

        // TODO: inject this via props?
        // tslint:disable:no-unused-expression
        new FeedSettingsActionCreator(this.getStore());
    }

    public componentWillMount(): void {
        FeedSettingsActionCreator.onInitialPivotLoad.invoke(this.state.hubViewState.selectedPivot.value);
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.props.fluxStore.addChangedListener(this.triggerRender);
        this.state.hubViewState.subscribe(this.onPivotChanging, HubViewStateEventNames.pivotChanging);
        this.state.permissionsFilter.subscribe(this.onPermissionsFilterChanged, FILTER_CHANGE_EVENT);
        this.state.viewsFilter.subscribe(this.onViewsFilterChanged, FILTER_CHANGE_EVENT);
        this.state.upstreamSourceFilter.subscribe(this.onUpstreamSourceFilterChanged, FILTER_CHANGE_EVENT);

        this.state.hubViewState.selectedPivot.subscribe(this.onPivotChanged);

        if (!this.props.upstreamHealthStatuses) {
            this.props.loadFeedMessageStarted();
        }
    }

    public componentWillUnmount(): void {
        this.state.permissionsFilter.unsubscribe(this.onPermissionsFilterChanged, FILTER_CHANGE_EVENT);
        this.state.viewsFilter.unsubscribe(this.onViewsFilterChanged, FILTER_CHANGE_EVENT);
        this.state.upstreamSourceFilter.unsubscribe(this.onUpstreamSourceFilterChanged, FILTER_CHANGE_EVENT);
        this.state.hubViewState.unsubscribe(this.onPivotChanging);
        this.state.hubViewState.selectedPivot.unsubscribe(this.onPivotChanged);
        [
            SettingsPivotKeys.details,
            SettingsPivotKeys.permissions,
            SettingsPivotKeys.views,
            SettingsPivotKeys.upstreams
        ].forEach((pivotKey: string) => {
            this._pivotUrls[pivotKey].dispose();
        });
        this.props.fluxStore.removeChangedListener(this.triggerRender);
        super.componentWillUnmount();
    }

    public componentDidUpdate(): void {
        if (this.state.isSavingChanges === false && this.isMoveUpOrDown === true) {
            this.hub.pivotBar.focus(PivotBarFocusItem.commands);
            this.isMoveUpOrDown = false;
        }

        if (!this.props.upstreamHealthStatuses) {
            this.props.loadFeedMessageStarted();
        }
    }

    public render(): JSX.Element {
        this._feedDetailsProps = MapIFeedSettingsStateToProps.ToFeedDetailsProps(this.state);
        this._permissionsProps = MapIFeedSettingsStateToProps.ToPermissionsProps(this.state);
        this._viewsProps = MapIFeedSettingsStateToProps.ToViewsProps(this.state);
        this._upstreamProps = MapIFeedSettingsStateToProps.ToUpstreamSettingsProps(
            this.state,
            this.props.upstreamHealthStatuses);

        return (
            <div className={"packages-controllerview settings-controller-view relative"}>
                {this.state.showWelcomeMessage() ? (
                    <WrappedZeroFeedsPage />
                ) : (
                        <div>
                            {/*hidden h1 for accessibility*/}
                            <h1 className="visibly-hidden">{PackageResources.FeedSettings_Title}</h1>
                            {this.state.messageBarMessage != null && (
                                <MessageBar
                                    onDismiss={() => {
                                        FeedSettingsActionCreator.onDismissMessageBar.invoke({});
                                    }}
                                    severity={this.state.messageBarType}
                                >
                                    {
                                        /* Ideally, there should be only 1 message bar but
                                                    existing message bar is operating under global store */
                                        this.state.messageBarMessage
                                    }
                                </MessageBar>
                            )}
                            {this.state.feed().deletedDate != null && this.state.messageBarMessage == null && (
                                <MessageBar severity={MessageBarSeverity.Error}>
                                    {Utils_String.format(
                                        PackageResources.FeedSettings_DeletedFeed_MessageBarMessage,
                                        new Date(this.state.feed().scheduledPermanentDeleteDate).toLocaleDateString()
                                    )}
                                </MessageBar>
                            )}
                            {this.state.showDiscardDialog && (
                                <Dialog
                                    lightDismiss={true}
                                    onDismiss={this._onDiscardDialogStayOnPage}
                                    titleProps={{ text: PackageResources.FeedSettings_DiscardDialog_Title }}
                                    footerButtonProps={[
                                        {
                                            text: PackageResources.FeedSettings_DiscardButton_Label,
                                            onClick: this._onUnsavedChangesDialogDiscard
                                        },
                                        {
                                            primary: true,
                                            text: PackageResources.FeedSettings_SaveButton_Label,
                                            onClick: this._onUnsavedChangesDialogSave
                                        }
                                    ]}
                                >
                                    {PackageResources.FeedSettings_DiscardDialog_Description}
                                </Dialog>
                            )}
                            <Hub
                                componentRef={(hub: IHub) => {
                                    this.hub = hub;
                                }}
                                className={"packages-hubcontent feed-settings-hub"}
                                hubViewState={this.state.hubViewState}
                                hideFullScreenToggle={true}
                                onRenderFilterBar={this.onRenderFilterBar}
                                showFilterBarInline={false}
                                viewActions={this._getViewActions()}
                                pivotHeaderAriaLabel={PackageResources.FeedSettings_Title}
                            >
                                <HubHeader
                                    title={PackageResources.FeedSettings_Title}
                                    breadcrumbItems={[
                                        {
                                            key: "selectedFeed",
                                            text: this.state.feed().name,
                                            onClick: (): void => {
                                                this.onBreadcrumbClick();
                                            }
                                        }
                                    ]}
                                />
                                <PivotBarItem
                                    name={PackageResources.FeedDetailsPane_FeedDetails}
                                    itemKey={SettingsPivotKeys.details}
                                    url={this._pivotUrls[SettingsPivotKeys.details]}
                                    commands={this.getPivotBarActions(SettingsPivotKeys.details)}
                                >
                                    <FeedDetailsPane {...this._feedDetailsProps} />
                                </PivotBarItem>
                                <PivotBarItem
                                    className="packages-pivotBarItem"
                                    name={PackageResources.FeedSettings_Permissions_Title}
                                    itemKey={SettingsPivotKeys.permissions}
                                    url={this._pivotUrls[SettingsPivotKeys.permissions]}
                                    commands={this.getPivotBarActions(SettingsPivotKeys.permissions)}
                                >
                                    <Permissions {...this._permissionsProps} />
                                </PivotBarItem>
                                {this.state.feed().deletedDate == null && (
                                    <PivotBarItem
                                        className="packages-pivotBarItem"
                                        name={PackageResources.FeedSettings_Views_Title}
                                        itemKey={SettingsPivotKeys.views}
                                        url={this._pivotUrls[SettingsPivotKeys.views]}
                                        commands={this.getPivotBarActions(SettingsPivotKeys.views)}
                                    >
                                        <Views {...this._viewsProps} />
                                    </PivotBarItem>
                                )}
                                {this.showUpstreamSettings() && this.state.feed().deletedDate == null && (
                                    <PivotBarItem
                                        className="packages-pivotBarItem"
                                        name={PackageResources.UpstreamSettings_Title}
                                        itemKey={SettingsPivotKeys.upstreams}
                                        url={this._pivotUrls[SettingsPivotKeys.upstreams]}
                                        commands={this.getPivotBarActions(SettingsPivotKeys.upstreams)}
                                    >
                                        <UpstreamSettingsPane {...this._upstreamProps} />
                                    </PivotBarItem>
                                )}
                            </Hub>
                        </div>
                    )}
            </div>
        );
    }

    private showUpstreamSettings(): boolean {
        if (this.state.feed().project != null) {
            // if project-scoped upstreams is off, all project-scoped feeds can't add upstreams
            if (!this._projectScopedUpstreamsEnabled) {
                return false;
            }
            // if project-scoped upstreams is on, only public feeds can't add upstreams.
            if (this.state.feed().project.visibility == "Public") {
                return false;
            }
        }
        return true;
    }

    private _getViewActions(): IPivotBarAction[] {
        let documentationHref: string = null;

        if (this.state.hubViewState.selectedPivot.value === SettingsPivotKeys.views) {
            documentationHref = FwLinks.ViewsDocs;
        } else if (this.state.hubViewState.selectedPivot.value === SettingsPivotKeys.upstreams) {
            documentationHref = FwLinks.UpstreamDocs;
        }

        if (documentationHref) {
            return [
                {
                    key: "help",
                    important: true,
                    iconProps: {
                        iconType: VssIconType.fabric,
                        iconName: "Unknown"
                    } as IVssIconProps,
                    href: documentationHref,
                    target: "_blank",
                    title: PackageResources.FeedSettings_Documentation,
                    ariaLabel: PackageResources.FeedSettings_DocumentationLink_AriaLabel
                } as IPivotBarAction
            ];
        }

        return [];
    }

    public getStore(): FeedStore {
        return this.props.fluxStore;
    }

    public getState(): IFeedSettingsState {
        return this.getStore().getFeedSettingsState();
    }

    private onRenderFilterBar = (): React.ReactElement<IFilterBarProps> => {
        const pivotKey = this.state.hubViewState.selectedPivot.value;
        switch (pivotKey) {
            case SettingsPivotKeys.permissions:
                return this.getPermissionsFilterBar();
            case SettingsPivotKeys.views:
                return this.getViewsFilterBar();
            case SettingsPivotKeys.upstreams:
                return <UpstreamSettingsFilterBar {...this._upstreamProps} />;
            default:
                return null;
        }
    };

    private getPermissionsFilterBar(): JSX.Element {
        return (
            <FilterBar className="packages-filterbar" filter={this.state.permissionsFilter}>
                <KeywordFilterBarItem
                    key={SettingsFilterItemKey.userOrGroup}
                    placeholder={Utils_String.format(
                        PackageResources.FeedSettings_FilterBy,
                        PackageResources.FeedSettings_Permissions_Grid_UserGroup
                    )}
                    filterItemKey={SettingsFilterItemKey.userOrGroup}
                />
                <PickListFilterBarItem
                    placeholder={PackageResources.FeedSettings_Permissions_Grid_Role}
                    filterItemKey={SettingsFilterItemKey.role}
                    selectionMode={SelectionMode.multiple}
                    getPickListItems={() => {
                        const roles = [
                            PackageResources.FeedSettings_Permissions_Role_Owner,
                            PackageResources.FeedSettings_Permissions_Role_Contributor,
                            PackageResources.FeedSettings_Permissions_Role_Collaborator
                        ];
                        roles.push(PackageResources.FeedSettings_Permissions_Role_Reader);
                        return roles;
                    }}
                />
            </FilterBar>
        );
    }

    private getViewsFilterBar(): JSX.Element {
        return (
            <FilterBar className="packages-filterbar" filter={this.state.viewsFilter}>
                <KeywordFilterBarItem
                    key={SettingsFilterItemKey.views}
                    placeholder={Utils_String.format(
                        PackageResources.FeedSettings_FilterBy,
                        PackageResources.FeedSettings_Views_Grid_View
                    )}
                    filterItemKey={SettingsFilterItemKey.views}
                />
            </FilterBar>
        );
    }

    private getPivotBarActions(pivotKey: string): IPivotBarAction[] {
        const pivotBarActions: IPivotBarAction[] = [];

        switch (pivotKey) {
            case SettingsPivotKeys.details:
                pivotBarActions.push(...this._getDetailsPivotBarActions());
                break;
            case SettingsPivotKeys.permissions:
                pivotBarActions.push(...this._getPermissionsPivotBarActions());
                break;
            case SettingsPivotKeys.views:
                pivotBarActions.push(...this._getViewsPivotBarActions());
                break;
            case SettingsPivotKeys.upstreams:
                pivotBarActions.push(...this._getUpstreamSettingsActions());
                break;
            default:
                break;
        }

        return pivotBarActions;
    }

    private pivotHasChanges(pivotKey: string): boolean {
        if (!this.state.isUserAdmin()) {
            return false;
        }

        switch (pivotKey) {
            case SettingsPivotKeys.details:
                return this._feedDetailsProps.hasChanges() || this._feedDetailsProps.hasValidationErrors();
        }
    }

    private _getDetailsPivotBarActions(): IPivotBarAction[] {
        const pivotBarActions: IPivotBarAction[] = [];
        if (this.state.feed().deletedDate == null) {
            pivotBarActions.push({
                key: "delete-feed",
                name: PackageResources.FeedDetailsPane_DeleteFeedButton_Label,
                important: true,
                iconProps: {
                    iconName: "Delete",
                    className: "delete-icon"
                },
                onClick: (): void => {
                    FeedSettingsActionCreator.showDeleteFeedDialog.invoke(null);
                },
                disabled: !this.state.isUserAdmin()
            });
        }

        if (this.state.feed().deletedDate != null) {
            pivotBarActions.push({
                key: "restore-feed",
                name: PackageResources.FeedDetailsPane_RestoreFeedButton_Label,
                important: true,
                iconProps: {
                    iconName: "bowtie-icon bowtie-recycle-bin-restore"
                },
                onClick: (): void => {
                    FeedSettingsActionCreator.onRestoreFeedClicked.invoke(null);
                },
                disabled: !this.state.isUserAdmin()
            });
            pivotBarActions.push({
                key: "permanentdelete-feed",
                name: PackageResources.FeedDetailsPane_PermanentlyDeleteFeedButton_Label,
                important: true,
                iconProps: {
                    iconName: "bowtie-icon bowtie-edit-delete",
                    className: "permanent-delete-icon"
                },
                onClick: (): void => {
                    FeedSettingsActionCreator.showPermanentDeleteFeedDialog.invoke(null);
                },
                disabled: !this.state.isUserAdmin()
            });
        }

        return pivotBarActions;
    }

    private _getPermissionsPivotBarActions(): IPivotBarAction[] {
        const isDeleteActionDisabled =
            !this.state.isUserAdmin() ||
            this.state.selectedPermissions.length === 0 ||
            this.state.isSavingChanges === true;

        const pivotBarActions: IPivotBarAction[] = [];
        if (this.state.feed().deletedDate == null) {
            pivotBarActions.push(
                {
                    key: "permissions-addUserOrGroup",
                    name: PackageResources.FeedSettings_Permissions_Grid_AddPermission_Label,
                    important: true,
                    iconProps: { iconName: "Add" },
                    onClick: (): void => {
                        FeedSettingsActionCreator.toggleAddUserOrGroupPanelDisplay.invoke(true /*open panel*/);
                    },
                    disabled: !this.state.isUserAdmin() || this.state.isSavingChanges === true
                },
                {
                    key: "permissions-delete",
                    name:  PackageResources.FeedSettings_Permissions_Grid_DeletePermission_Label,
                    important: true,
                    iconProps: {
                        iconName: "Delete",
                        className: isDeleteActionDisabled ? "" : "delete-icon"
                    },
                    onClick: (): void => {
                        FeedSettingsActionCreator.showDeletePermissionsDialog.invoke(null);
                    },
                    disabled: isDeleteActionDisabled
                },
                {
                    key: "permissions-addCollectionScopedBuildPermission",
                    name: PackageResources.FeedSettings_Permissions_Grid_AddCollectionScopedPermission_Label,
                    disabled:
                        this._permissionsProps.collectionScopedPermissionIsDisplayed ||
                        !this.state.isUserAdmin() ||
                        this.state.isSavingChanges === true,
                    iconProps: {
                        iconName: "bowtie-build",
                        iconType: VssIconType.bowtie /* replace this with fabric icon when available*/
                    },
                    onClick: (): void => {
                        FeedSettingsActionCreator.addScopedBuildPermissionClicked.invoke(true /* collectionScope */);
                    }
                }
            );
        }

        if (this._permissionsProps.projectScopedPermissionExists) {
            pivotBarActions.push({
                key: "permissions-addProjectScopedBuildPermission",
                name: PackageResources.FeedSettings_Permissions_Grid_AddProjectScopedPermission_Label,
                disabled:
                    this._permissionsProps.projectScopedPermissionIsDisplayed ||
                    !this.state.isUserAdmin() ||
                    this.state.isSavingChanges === true,
                iconProps: { iconName: "bowtie-build", iconType: VssIconType.bowtie },
                onClick: (): void => {
                    FeedSettingsActionCreator.addScopedBuildPermissionClicked.invoke(false /* projectScope */);
                }
            });
        }

        return pivotBarActions;
    }

    private _getViewsPivotBarActions(): IPivotBarAction[] {
        const isDeleteActionDisabled =
            !this.state.isUserAdmin() ||
            this.state.selectedViews.length === 0 ||
            this.state.selectedViews.some((view: FeedView) => {
                return view.type === FeedViewType.Implicit;
            }) ||
            this.state.isSavingChanges === true;

        const pivotBarActions: IPivotBarAction[] = [
            {
                key: "add-views",
                name: PackageResources.FeedSettings_Views_AddView_Label,
                important: true,
                iconProps: { iconName: "Add" },
                onClick: (): void => {
                    FeedSettingsActionCreator.toggleViewPanelDisplay.invoke({ isOpen: true, isEditing: false });
                },
                disabled: !this.state.isUserAdmin()
            }
        ];

        pivotBarActions.push({
            key: "edit-views",
            name: PackageResources.FeedSettings_Views_EditView_Label,
            important: true,
            iconProps: { iconName: "Edit" },
            onClick: (): void => {
                FeedSettingsActionCreator.toggleViewPanelDisplay.invoke({ isOpen: true, isEditing: true });
            },
            disabled:
                !this.state.isUserAdmin() ||
                this.state.selectedViews.length !== 1 ||
                this.state.isSavingChanges === true
        });

        pivotBarActions.push({
            key: "views-delete",
            name: PackageResources.FeedSettings_Views_DeleteButton_Label,
            important: true,
            iconProps: {
                iconName: "Delete",
                className: isDeleteActionDisabled ? "" : "delete-icon"
            },
            onClick: (): void => {
                FeedSettingsActionCreator.showDeleteViewsDialog.invoke(null);
            },
            disabled: isDeleteActionDisabled
        });

        pivotBarActions.push({
            key: "views-make-default",
            name: PackageResources.FeedSettings_Views_SetDefaultViewButton_Label,
            important: true,
            iconProps: {
                iconName: "CheckMark"
            },
            onClick: (): void => {
                const view = this.state.selectedViews[0];
                FeedSettingsActionCreator.markDefaultViewClicked.invoke(view);
            },
            disabled:
                !this.state.isUserAdmin() ||
                this.state.selectedViews.length !== 1 ||
                this.state.isSavingChanges === true ||
                this.state.feed().defaultViewId === this.state.selectedViews[0].id
        });

        return pivotBarActions;
    }

    private _getUpstreamSettingsActions(): IPivotBarAction[] {
        const isDeleteActionDisabled =
            !this.state.isUserAdmin() ||
            this.state.selectedUpstreamSources.length === 0 ||
            this.state.isSavingChanges === true;

        const isReorderingActionDisabled =
            !this.state.isUserAdmin() ||
            this.state.selectedUpstreamSources.length === 0 || // no row is selected
            this.state.selectedUpstreamSources.length !== 1 || // more than 1 row is selected
            this.state.isSavingChanges === true;

        let moveUpDisabled = isReorderingActionDisabled;
        let moveDownDisabled = isReorderingActionDisabled;
        let indexInGrid = -1;

        if (!isReorderingActionDisabled) {
            // list might be filtered, so instead of state.feed().upstreamSources use upstreamSourceRow
            indexInGrid = findIndex(
                this._upstreamProps.upstreamSourceRows,
                (upstreamSourceRow: IUpstreamSettingsRowData) => {
                    return upstreamSourceRow.upstreamSource.id === this.state.selectedUpstreamSources[0].id;
                }
            );
            moveUpDisabled = indexInGrid === 0;
            moveDownDisabled = indexInGrid === this._upstreamProps.upstreamSourceRows.length - 1;
        }

        const pivotBarActions: IPivotBarAction[] = [
            {
                key: SettingsPivotKeys.upstreams + "-addUpstreamSource",
                name: PackageResources.UpstreamSettings_AddUpstreamSourceButton_Label,
                important: true,
                iconProps: { iconName: "Add", iconType: VssIconType.fabric },
                onClick: (): void => {
                    FeedSettingsActionCreator.openAddUpstreamPanelRequested.invoke({});
                },
                disabled: !this.state.isUserAdmin() || this.state.isSavingChanges
            },
            {
                key: "views-upstreamsources",
                name: PackageResources.FeedSettings_UpstreamSources_DeleteButton_Label,
                important: true,
                iconProps: {
                    iconName: "Delete",
                    className: isDeleteActionDisabled ? "" : "delete-icon"
                },
                onClick: (): void => {
                    FeedSettingsActionCreator.showDeleteUpstreamSourcesDialog.invoke(null);
                },
                disabled: isDeleteActionDisabled
            },
            {
                key: "move-up",
                name: PackageResources.FeedSettings_Upstreams_MoveUp_ButtonLabel,
                important: true,
                iconProps: {
                    iconName: "SortUp"
                },
                onClick: (): void => {
                    // because list might be filtered, send item to swap with
                    // pass lower index item in from
                    FeedSettingsActionCreator.reorderUpstreamSources.invoke({
                        fromIndexOfUpstreamSource: this._upstreamProps.upstreamSourceRows[indexInGrid - 1]
                            .upstreamSource,
                        toIndexOfUpstreamSource: this.state.selectedUpstreamSources[0]
                    });
                    this.isMoveUpOrDown = true;
                },
                disabled: moveUpDisabled
            },
            {
                key: "move-down",
                name: PackageResources.FeedSettings_Upstreams_MoveDown_ButtonLabel,
                important: true,
                iconProps: {
                    iconName: "SortDown"
                },
                onClick: (): void => {
                    // because list might be filtered, send item to swap with
                    // pass lower index item in from
                    FeedSettingsActionCreator.reorderUpstreamSources.invoke({
                        fromIndexOfUpstreamSource: this.state.selectedUpstreamSources[0],
                        toIndexOfUpstreamSource: this._upstreamProps.upstreamSourceRows[indexInGrid + 1].upstreamSource
                    });
                    this.isMoveUpOrDown = true;
                },
                disabled: moveDownDisabled
            }
        ];

        return pivotBarActions;
    }

    /**
     * If there are changes, show discard dialog instead of navigating away
     */
    private onBreadcrumbClick = (): void => {
        const currentPivotHasChanges: boolean = this.pivotHasChanges(this.state.currentPivotKey);
        if (currentPivotHasChanges) {
            FeedSettingsActionCreator.showDiscardChangesDialog.invoke(null);
        } else {
            LegacyActions.FeedBreadcrumbSelected.invoke(null);
        }
    };

    /**
     * Stay in current pivot if there are pending changes and show discard dialog
     * @param toPivotKey Pivot users wants to switch to
     */
    private onPivotChanging = (toPivotKey: string): void => {
        const currentPivotHasChanges: boolean = this.pivotHasChanges(this.state.currentPivotKey);
        if (currentPivotHasChanges) {
            this.state.hubViewState.selectedPivot.value = this.state.currentPivotKey;
            FeedSettingsActionCreator.showDiscardChangesDialog.invoke(null);
        }
    };

    /**
     * Store the new pivot user switched to
     * @param newPivotKey Pivot user switched to
     */
    private onPivotChanged = (newPivotKey: string): void => {
        FeedSettingsActionCreator.onPivotChanged.invoke(newPivotKey);
    };

    private onPermissionsFilterChanged = (changedState: IFilterState): void => {
        // trigger re-render
        this.setState({});

        CustomerIntelligenceHelper.publishEvent(this.context.pageContext, FeedSettingsCi.FilterChanged, {
            filter: "permissions"
        });
    };

    private onViewsFilterChanged = (changedState: IFilterState): void => {
        // trigger re-render
        this.setState({});

        CustomerIntelligenceHelper.publishEvent(this.context.pageContext, FeedSettingsCi.FilterChanged, {
            filter: "views"
        });
    };

    private onUpstreamSourceFilterChanged = (changedState: IFilterState): void => {
        // trigger re-render
        this.setState({});

        CustomerIntelligenceHelper.publishEvent(this.context.pageContext, FeedSettingsCi.FilterChanged, {
            filter: "upstreamsources"
        });
    };

    private _onUnsavedChangesDialogDiscard = (): void => {
        FeedSettingsActionCreator.onDiscardChangesClicked.invoke(null);
    };

    private _onUnsavedChangesDialogSave = (): void => {
        switch (this.state.hubViewState.selectedPivot.value) {
            case SettingsPivotKeys.details:
                FeedSettingsActionCreator.saveFeedDetailsChanges.invoke(null);
                break;
        }
    };

    private _onDiscardDialogStayOnPage = (): void => {
        FeedSettingsActionCreator.onStayOnCurrentPivotClicked.invoke(null);
    };

    private _pivotUrls: { [pivotKey: string]: IObservableViewStateUrl };
    private _feedDetailsProps: IFeedDetailsPaneProps;
    private _permissionsProps: IPermissionsProps;
    private _viewsProps: IViewsProps;
    private _upstreamProps: IUpstreamSettingsPaneProps;
    private hub: IHub;
    // as of now, only doing this for Move up or Move down buttons in Upstream Sources pivot
    // not adding it to state because it will cause re-render
    private isMoveUpOrDown: boolean;
    private triggerRender = () => this.setState(this.getState());
}

function mapStateToProps(state: IFeedHubViewAwareState): ISettingsControllerViewProps2 {
    return {
        upstreamHealthStatuses: getUpstreamHealthStatuses(state)
    };
}

const ConnectedSettingsControllerView = connect(mapStateToProps, Actions)(SettingsControllerView);

function LegacySettingsControllerView(props: ILegacyControllerViewProps) {
    return <ConnectedSettingsControllerView fluxStore={props.feedFluxStore} />;
}

VssComponent.register("feedsettings", LegacySettingsControllerView);
